import React, { useState, useEffect, useCallback } from 'react';
import { Download, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { getLedgerEntries, exportLedgerCsv } from '../../api/billingApi';

const ENTRY_TYPES = ['', 'model', 'runtime', 'tool', 'storage', 'platform_fee', 'expiry', 'byok_usage'];

/**
 * UsageExplorer — paginated ledger table with filters and CSV export.
 *
 * Props:
 *   orgId  — string UUID
 */
function UsageExplorer({ orgId }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entryType, setEntryType] = useState('');

  const PAGE_SIZE = 25;

  const fetch = useCallback(async (p = 1) => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getLedgerEntries({
        orgId,
        from: from || undefined,
        to: to || undefined,
        entryType: entryType || undefined,
        page: p,
        pageSize: PAGE_SIZE,
      });
      setEntries(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, from, to, entryType]);

  useEffect(() => { fetch(1); }, [fetch]);

  const handleExport = () => {
    const uri = exportLedgerCsv(entries);
    const a = document.createElement('a');
    a.href = uri;
    a.download = `usage-${orgId}-page${page}.csv`;
    a.click();
  };

  const toGbp = (credits) => credits != null ? `£${(parseFloat(credits) / 100).toFixed(4)}` : '—';
  const fmtDate = (dt) => dt ? new Date(dt).toLocaleString() : '—';

  const typeColors = {
    model: 'bg-blue-100 text-blue-700',
    runtime: 'bg-purple-100 text-purple-700',
    tool: 'bg-orange-100 text-orange-700',
    storage: 'bg-gray-100 text-gray-600',
    platform_fee: 'bg-yellow-100 text-yellow-700',
    expiry: 'bg-red-100 text-red-600',
    byok_usage: 'bg-green-100 text-green-700',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mr-auto">
          Usage Explorer
        </h3>

        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={entryType}
            onChange={e => setEntryType(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700"
          >
            {ENTRY_TYPES.map(t => (
              <option key={t} value={t}>{t || 'All types'}</option>
            ))}
          </select>
        </div>

        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700"
          title="From"
        />
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700"
          title="To"
        />

        <button
          onClick={handleExport}
          disabled={entries.length === 0}
          className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded transition-colors disabled:opacity-40"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Table */}
      {error && (
        <div className="p-4 text-sm text-red-600">{error}</div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Model</th>
                <th className="px-4 py-2 font-medium text-right">In tokens</th>
                <th className="px-4 py-2 font-medium text-right">Out tokens</th>
                <th className="px-4 py-2 font-medium text-right">Cost (credits)</th>
                <th className="px-4 py-2 font-medium text-right">Cost (£)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    No entries found
                  </td>
                </tr>
              ) : entries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${typeColors[e.entry_type] || 'bg-gray-100 text-gray-600'}`}>
                      {e.entry_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600 max-w-[160px] truncate" title={e.model_name || ''}>
                    {e.model_name || '—'}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{(e.input_tokens || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{(e.output_tokens || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{parseFloat(e.total_cost_credits || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{toGbp(e.total_cost_credits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>{total.toLocaleString()} entries</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetch(page - 1)}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>Page {page} of {pages}</span>
            <button
              onClick={() => fetch(page + 1)}
              disabled={page >= pages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsageExplorer;
